import { useCallback, useEffect, useState } from 'react';
import type { ReferenceKind, ReferenceTermView } from '@cercle/shared';
import { useApi } from '../app-context';

/**
 * Le référentiel, avec son cache.
 *
 * Le référentiel vit maintenant en base, mais l'application se veut utilisable en cave —
 * là où le réseau est mauvais quand il existe. La liste est donc conservée dans le
 * stockage local et servie immédiatement au montage, puis rafraîchie dès que l'appel
 * aboutit. Un membre hors-ligne garde les termes de sa dernière visite, ce qui suffit à
 * noter ; il ne peut simplement pas en créer de nouveau.
 *
 * `localStorage` et non IndexedDB : quelques dizaines de chaînes, lues à chaque ouverture
 * d'un champ. La file de notes, elle, a besoin d'IndexedDB — pas ce cache-ci.
 */
const CACHE_PREFIX = 'cercle:reference:';

export interface ReferenceState {
  terms: ReferenceTermView[];
  /** Vrai tant qu'aucune donnée, ni cache ni réseau, n'est disponible. */
  loading: boolean;
  /** Ajoute un terme et le renvoie. Échoue hors-ligne, volontairement. */
  add: (label: string, family?: string | null) => Promise<ReferenceTermView>;
}

export function useReference(kind: ReferenceKind): ReferenceState {
  const api = useApi();
  const [terms, setTerms] = useState<ReferenceTermView[]>(() => readCache(kind));
  const [loading, setLoading] = useState(() => readCache(kind).length === 0);

  useEffect(() => {
    let alive = true;
    void api.reference
      .list(kind)
      .then((fresh) => {
        if (!alive) return;
        setTerms(fresh);
        writeCache(kind, fresh);
      })
      // Hors-ligne, on garde le cache : c'est exactement ce pour quoi il existe.
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api, kind]);

  const add = useCallback(
    async (label: string, family?: string | null): Promise<ReferenceTermView> => {
      const created = await api.reference.add({ kind, label, family: family ?? null });
      setTerms((current) => {
        // L'API est idempotente : « cap-vert » renvoie le « Cap-Vert » existant. On ne
        // l'ajoute donc à la liste que s'il n'y figure pas déjà.
        if (current.some((term) => term.id === created.id)) return current;
        const next = [...current, created];
        writeCache(kind, next);
        return next;
      });
      return created;
    },
    [api, kind],
  );

  return { terms, loading, add };
}

function readCache(kind: ReferenceKind): ReferenceTermView[] {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + kind);
    return raw ? (JSON.parse(raw) as ReferenceTermView[]) : [];
  } catch {
    // Un cache illisible n'est pas une panne : on repart de zéro.
    return [];
  }
}

function writeCache(kind: ReferenceKind, terms: ReferenceTermView[]): void {
  try {
    localStorage.setItem(CACHE_PREFIX + kind, JSON.stringify(terms));
  } catch {
    // Quota plein ou navigation privée : le cache est un confort, pas une dépendance.
  }
}
